import "features/messages/src/styles/TeamChannelHeader/index.scss";
import { useCallback } from "react";
import { useChannelActionContext, useChannelStateContext, useChatContext } from "stream-chat-react";

import { Avatar, Popconfirm } from "antd";
import { InboxOutlined, InfoCircleOutlined, PushpinOutlined } from "@ant-design/icons";
import styled from "styled-components";

import UserBadge from "../UserBadge/UserBadge";
import { useWorkspaceController } from "../../context/WorkspaceController";

const CountTile = styled(Avatar)`
  background-color: #8c8c8c;
  color: #fff;
  font-weight: 600;
  font-size: 13px;
  flex-shrink: 0;
`;

export default function TeamChannelHeader() {
  const { displayWorkspace } = useWorkspaceController();
  const { client } = useChatContext();
  const { channel, watcher_count: watcherCount } = useChannelStateContext();
  const { closeThread } = useChannelActionContext();
  const { togglePinnedMessageListOpen } = useWorkspaceController();

  const teamHeader = `# ${channel?.data?.name || channel?.data?.id || "random"}`;

  const openChannelEditPanel = useCallback(() => {
    displayWorkspace("Admin-Channel-Edit");
  }, [displayWorkspace]);

  const onPinIconClick = useCallback(
    (event) => {
      closeThread?.(event);
      togglePinnedMessageListOpen();
    },
    [closeThread, togglePinnedMessageListOpen],
  );

  const onArchiveConfirm = useCallback(async () => {
    if (channel?.archive) {
      await channel.archive();
    }
    if (channel?.hide) {
      await channel.hide();
    }
  }, [channel]);

  const getMessagingHeader = () => {
    const members = Object.values(channel.state.members).filter(
      ({ user }) => user?.id !== client.userID,
    );

    if (!members.length) {
      return (
        <div className="workspace-header__block">
          <UserBadge name="?" size={36} />
          <p className="team-channel-header__name user">Unknown user</p>
        </div>
      );
    }

    if (members.length === 1) {
      const { user } = members[0];
      const displayName = user?.name || user?.email || user?.id || "Unknown user";
      return (
        <div className="workspace-header__block">
          <UserBadge name={displayName} image={user?.image} userId={user?.id} size={36} />
          <p className="team-channel-header__name user">{displayName}</p>
        </div>
      );
    }

    const firstNameOf = (user) => {
      const full = user?.name || user?.email || user?.id || "Unknown";
      return full.split(/\s+/)[0];
    };
    const firstNames = members.slice(0, 3).map(({ user }) => firstNameOf(user));
    const extra = members.length - 3;
    const nameText =
      channel.data?.name ||
      (extra > 0 ? `${firstNames.join(", ")}, +${extra} more` : firstNames.join(", "));

    return (
      <div className="workspace-header__block">
        <CountTile shape="square" size={36}>
          {members.length}
        </CountTile>
        <p className="team-channel-header__name user">{nameText}</p>
      </div>
    );
  };

  const getWatcherText = (watchers) => {
    if (!watchers) return "No users online";
    if (watchers === 1) return "1 user online";
    return `${watchers} users online`;
  };

  return (
    <div className="team-channel-header__container">
      {channel.type === "messaging" ? (
        getMessagingHeader()
      ) : (
        <div className="workspace-header__block">
          <div className="team-channel-header__name workspace-header__title">{teamHeader}</div>
          <button onClick={openChannelEditPanel} type="button">
            <InfoCircleOutlined />
          </button>
        </div>
      )}
      <div className="workspace-header__block">
        <div className="workspace-header__subtitle">{getWatcherText(watcherCount)}</div>
        <button className="workspace-header__subtitle" onClick={onPinIconClick} type="button">
          <PushpinOutlined />
          Pins
        </button>
        <Popconfirm
          title="Archive this conversation?"
          description="It will be archived and removed from your channel list. You can find it again via search."
          onConfirm={onArchiveConfirm}
          okText="Archive"
          cancelText="Cancel"
        >
          <button className="workspace-header__subtitle" type="button">
            <InboxOutlined />
            Archive
          </button>
        </Popconfirm>
      </div>
    </div>
  );
}
